/**
 * LAS DOS PANTALLAS DE PREFERENCIAS DEL ALTA (24-10).
 *
 * Sustituyen a la de «¿Existe algún alimento... que no quieras introducir en ningún caso?»:
 *
 *  - `modo="evitar"`: lo que NO quiere. Categorías enteras (`avoided_categories`) o alimentos
 *    sueltos por su nombre (`avoided_keywords`), lo mismo que guardaba la pantalla de antes.
 *    Esos alimentos no se le ofrecen ni para montar comidas ni dentro de un menú.
 *  - `modo="preferir"`: lo que MÁS le gusta. SOLO CATEGORÍAS desde el 04-11
 *    (`food_preferences`, las que salen en el último paso de montar una comida, cuando le
 *    faltan macros para cuadrarla): ni se eligen alimentos sueltos ni se enseñan. Vienen
 *    marcadas las de siempre (`DEFAULT_PREFERENCES`) y las grasas de buena calidad no se
 *    pueden quitar. Entre todas tienen que llegar a 20 g de proteína y 20 g de hidratos: la
 *    misma regla que la pantalla de Preferencias (`lib/preferenciasDeComida`), comprobada
 *    por el mismo motor.
 *
 * Las dos con un selector de categoría (combobox, con buscador) en lugar de la fila de
 * pastillas. La de evitar, además, con los filtros de preparación del pantallazo del 24-10
 * y la lista de alimentos de la categoría elegida; la de preferir marca y desmarca varias
 * categorías desde el propio selector.
 *
 * Todo vive en las respuestas del cuestionario (`answers` / `set`): se guarda con el
 * borrador al avanzar y viaja con el alta al terminar. A nivel de módulo, como el resto de
 * piezas del cuestionario con campos de texto, para que no pierdan el foco al teclear.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, ChevronDown, Search, Lock } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { PREFERENCE_CATEGORIES, EJEMPLOS } from '../nutrition/PreferencesSetup';
import {
    OBLIGATORY_PREFERENCES, preferidasDePartida, preguntaDeLos20g, noLlegaALos20g,
} from '../../lib/preferenciasDeComida';
import { conNombreCorto, PREPARATIONS } from '../nutrition/BuildMealModal';

const CAMPO = 'h-[52px] rounded-[12px] bg-[#161616] border border-[#2a2a2a] px-4 text-base '
    + 'text-[#f2f0ed] placeholder:text-[#5f5a55] focus-visible:ring-0 focus-visible:ring-offset-0 '
    + 'focus-visible:border-[#ff6a1f] focus:border-[#ff6a1f]';

// Los nombres de los filtros, como en el pantallazo: los de `PREPARATIONS` donde caben, y
// estos cuatro, que allí se leen distinto.
const NOMBRE_DEL_FILTRO = {
    PRO: 'Marca recomendada',
    POL: 'En polvo',
    SNA: 'Snacks para llevar',
    MIN: '1 minuto al micro',
};

const aPalabra = (nombre) => String(nombre || '').trim().toLowerCase();

// ── El selector de categoría ─────────────────────────────────────────────────────────
// Un botón que dice cuál está elegida y, al abrirlo, la lista de categorías con un buscador
// arriba. `estado(id)` devuelve la marca de cada una (fuera / preferida), para verla sin
// tener que abrirla.
// `varias`: la lista se queda abierta al pulsar y cada pulsación marca o desmarca (lo usa
// la pantalla de preferidos, que solo elige categorías). `sinEjemplos`: sin la línea de
// alimentos de ejemplo debajo de cada categoría. `rotulo`: lo que dice el botón cerrado.
//
// LAS OBLIGATORIAS, CON CANDADO Y SIN COGERSE (test 4.4 de Jesús, 04-11): las grasas de
// buena calidad salen en la lista de las dos pantallas, pero con un candado y sin que se
// puedan pulsar. En excluidos no se pueden quitar; en preferidos van siempre marcadas.
const SelectorDeCategoria = ({ categorias, elegida, onElegir, estado, varias = false,
                               sinEjemplos = false, rotulo = 'Elige una categoría de alimentos' }) => {
    const [abierto, setAbierto] = useState(false);
    const [filtro, setFiltro] = useState('');
    const caja = useRef(null);
    useEffect(() => {
        if (!abierto) return undefined;
        const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
        document.addEventListener('pointerdown', fuera);
        return () => document.removeEventListener('pointerdown', fuera);
    }, [abierto]);
    const visibles = categorias.filter(c => {
        const f = filtro.trim().toLowerCase();
        if (!f) return true;
        return `${c.label} ${sinEjemplos ? '' : EJEMPLOS[c.id] || ''}`.toLowerCase().includes(f);
    });
    const cat = varias ? null : categorias.find(c => c.id === elegida) || null;
    return (
        <div className="relative" ref={caja}>
            <button type="button" onClick={() => setAbierto(a => !a)} data-testid="selector-categoria"
                aria-haspopup="listbox" aria-expanded={abierto}
                className={`w-full h-[52px] rounded-[12px] border-[1.5px] px-4 flex items-center gap-3 text-left transition-colors ${
                    abierto ? 'border-[#ff6a1f] bg-[#161616]' : 'border-[#2a2a2a] bg-[#161616] hover:border-[#3a3a3a]'}`}>
                {cat?.icon && <FontAwesomeIcon icon={cat.icon} className="w-4 h-4 text-[#ff6a1f] flex-none" />}
                <span className={`flex-1 min-w-0 truncate text-base ${cat ? 'text-[#f2f0ed] font-semibold' : 'text-[#5f5a55]'}`}>
                    {cat ? cat.label : rotulo}
                </span>
                <ChevronDown className={`w-4 h-4 flex-none text-[#8a8580] transition-transform ${abierto ? 'rotate-180' : ''}`} />
            </button>
            {abierto && (
                <div className="absolute z-40 mt-2 w-full rounded-[14px] border border-[#2a2a2a] bg-[#111] shadow-2xl overflow-hidden"
                    role="listbox" data-testid="selector-categoria-lista">
                    <div className="p-2 border-b border-[#222]">
                        <div className="relative">
                            <Search className="w-4 h-4 text-[#5f5a55] absolute left-3 top-1/2 -translate-y-1/2" />
                            <input value={filtro} onChange={e => setFiltro(e.target.value)} autoFocus
                                placeholder="Buscar categoría"
                                className="w-full h-10 rounded-[10px] bg-[#161616] border border-[#2a2a2a] pl-9 pr-3 text-sm text-[#f2f0ed] placeholder:text-[#5f5a55] outline-none focus:border-[#ff6a1f]" />
                        </div>
                    </div>
                    <ul className="max-h-[320px] overflow-y-auto py-1">
                        {visibles.map(c => {
                            const marca = estado ? estado(c.id) : null;
                            const bloqueada = OBLIGATORY_PREFERENCES.includes(c.id);
                            const activa = varias ? Boolean(marca) : c.id === elegida;
                            return (
                                <li key={c.id}>
                                    <button type="button" role="option" aria-selected={activa}
                                        data-testid={`categoria-${c.id}`}
                                        disabled={bloqueada} aria-disabled={bloqueada}
                                        title={bloqueada ? 'El método las necesita: no se pueden cambiar.' : undefined}
                                        onClick={() => {
                                            if (bloqueada) return;
                                            onElegir(c.id);
                                            if (!varias) { setAbierto(false); setFiltro(''); }
                                        }}
                                        className={`w-full px-3 py-2.5 flex items-center gap-3 text-left ${
                                            bloqueada ? 'cursor-not-allowed' : 'hover:bg-[#1c1c1c]'} ${activa ? 'bg-[#1f1510]' : ''}`}>
                                        <span className="w-8 h-8 rounded-full bg-[#222] flex items-center justify-center flex-none text-[#d9d5d0]">
                                            {c.icon && <FontAwesomeIcon icon={c.icon} className="w-3.5 h-3.5" />}
                                        </span>
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-sm font-semibold text-[#f2f0ed] truncate">{c.label}</span>
                                            {!sinEjemplos && EJEMPLOS[c.id] && <span className="block text-xs text-[#8a8580] truncate">{EJEMPLOS[c.id]}</span>}
                                        </span>
                                        {marca && (
                                            <span className={`flex-none text-[11px] font-bold uppercase tracking-wide ${
                                                marca.tono === 'rojo' ? 'text-red-400' : 'text-[#ff6a1f]'}`}>{marca.texto}</span>
                                        )}
                                        {bloqueada && (
                                            <span className="flex-none inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#8a8580]"
                                                data-testid={`candado-${c.id}`}>
                                                <Lock className="w-3.5 h-3.5" /> Se ofrecen siempre
                                            </span>
                                        )}
                                        {varias && !bloqueada && (
                                            <span className={`w-[18px] h-[18px] rounded-[4px] flex items-center justify-center flex-none ${
                                                marca ? 'bg-[#ff6a1f]' : 'border-[1.5px] border-[#4a4a4a]'}`}>
                                                {marca && <Check className="w-3 h-3 text-[#140a04]" strokeWidth={3.5} />}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                        {!visibles.length && (
                            <li className="px-3 py-3 text-sm text-[#8a8580]">Ninguna categoría se llama así.</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

// ── Los filtros de preparación (pantallazo del 24-10) ────────────────────────────────
const FiltrosDePreparacion = ({ disponibles, marcados, onCambiar }) => {
    if (!disponibles.length) return null;
    const alternar = (v) => onCambiar(marcados.includes(v) ? marcados.filter(x => x !== v) : [...marcados, v]);
    return (
        <div data-testid="filtros-preparacion">
            <div className="flex items-center justify-between gap-3 mb-2.5">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8a8580]">
                    Filtrar · puedes elegir varios
                </span>
                {marcados.length > 0 && (
                    <button type="button" onClick={() => onCambiar([])} data-testid="quitar-filtros"
                        className="text-[13px] font-bold text-[#ff6a1f] hover:text-[#ff8a4d]">
                        Quitar filtros ({marcados.length})
                    </button>
                )}
            </div>
            <div className="flex flex-wrap gap-2">
                {disponibles.map(pr => {
                    const on = marcados.includes(pr.value);
                    return (
                        <button key={pr.value} type="button" onClick={() => alternar(pr.value)}
                            aria-pressed={on} data-testid={`filtro-${pr.value}`}
                            className={`inline-flex items-center gap-2 h-10 pl-3 pr-4 rounded-full border-[1.5px] text-[14px] font-bold transition-colors ${
                                on ? 'border-[#ff6a1f] bg-[#2a1508] text-[#f2f0ed]'
                                    : 'border-[#2a2a2a] bg-[#161616] text-[#8a8580] hover:text-[#d9d5d0]'}`}>
                            <span className={`w-[18px] h-[18px] rounded-[4px] flex items-center justify-center flex-none ${
                                on ? 'bg-[#ff6a1f]' : 'border-[1.5px] border-[#4a4a4a]'}`}>
                                {on && <Check className="w-3 h-3 text-[#140a04]" strokeWidth={3.5} />}
                            </span>
                            {NOMBRE_DEL_FILTRO[pr.value] || pr.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// La lista de alimentos de una categoría, con sus filtros. `boton(alimento)` dice qué
// enseña cada fila a la derecha; la pide quien la usa porque cambia por modo.
const useAlimentosDeCategoria = (api, cat, preps) => {
    const [lista, setLista] = useState({ alimentos: [], preps: [], cargando: false });
    useEffect(() => {
        if (!cat) { setLista({ alimentos: [], preps: [], cargando: false }); return undefined; }
        let vivo = true;
        setLista(l => ({ ...l, cargando: true }));
        const params = new URLSearchParams({ q: '', category: (cat.prefixes || []).join(','), limit: '100' });
        if (preps.length) params.set('tag', preps.join(','));
        api.get(`/calculator/search?${params}`)
            .then(r => {
                if (!vivo) return;
                // Como en el modo manual de Nutrición: sin hueco de macros, por orden alfabético.
                const alimentos = [...(r.data?.alimentos || [])]
                    .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));
                setLista({ alimentos, preps: r.data?.available_preps || [], cargando: false });
            })
            .catch(() => { if (vivo) setLista({ alimentos: [], preps: [], cargando: false }); });
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cat?.id, preps.join(',')]);
    return lista;
};

const ListaDeAlimentos = ({ lista, fila }) => (
    lista.cargando ? (
        <div className="space-y-2" data-testid="alimentos-cargando">
            {[0, 1, 2].map(n => <div key={n} className="h-10 rounded-lg bg-[#222] animate-pulse" />)}
        </div>
    ) : lista.alimentos.length ? (
        <ul className="max-h-[40vh] overflow-y-auto divide-y divide-white/10 pr-1" data-testid="alimentos-de-la-categoria">
            {lista.alimentos.map((a, n) => <li key={a.id ?? n}>{fila(a)}</li>)}
        </ul>
    ) : (
        <p className="text-sm text-[#a8a29c]">No hay alimentos con esos filtros.</p>
    )
);

const Pastilla = ({ children, tono = 'naranja', onQuitar, etiquetaQuitar }) => (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
        tono === 'rojo' ? 'bg-red-500/10 border-red-500/40 text-red-400'
            : 'bg-[#ff6a1f]/10 border-[#ff6a1f]/40 text-[#ff8a4d]'}`}>
        {children}
        {onQuitar && (
            <button type="button" onClick={onQuitar} aria-label={etiquetaQuitar}
                className={tono === 'rojo' ? 'hover:text-red-200' : 'hover:text-white'}>×</button>
        )}
    </span>
);

// ═══════════════════════════════════════════════════════════════════════════════
// PANTALLA 1 · LO QUE NO QUIERE
// ═══════════════════════════════════════════════════════════════════════════════
export const ExclusionesDelAlta = ({ answers, set, api }) => {
    const [buscando, setBuscando] = useState('');
    const [abierta, setAbierta] = useState(null);
    const [preps, setPreps] = useState([]);
    const evitadas = answers.avoided_categories || [];
    const palabras = answers.avoided_keywords || [];
    // LAS GRASAS DE BUENA CALIDAD NO SE PUEDEN ELEGIR AQUÍ (Jesús, 04-11, test 4.4): salen
    // en la lista con su candado y sin poder pulsarse (lo hace el selector), así que ni se
    // vetan enteras ni se abre su lista de alimentos para quitarlos uno a uno.
    const categorias = useMemo(() => conNombreCorto(PREFERENCE_CATEGORIES), []);
    const cat = categorias.find(c => c.id === abierta) || null;
    const lista = useAlimentosDeCategoria(api, cat, preps);
    const vetable = (id) => !OBLIGATORY_PREFERENCES.includes(id);
    // Un borrador de antes que las tuviera vetadas: fuera al llegar.
    useEffect(() => {
        if (evitadas.some(id => !vetable(id))) set('avoided_categories', evitadas.filter(vetable));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const elegir = (id) => { setPreps([]); setAbierta(id); };
    const alternarCategoria = (id) => {
        if (!vetable(id)) { toast.info('Las grasas de buena calidad no se pueden evitar: el método las necesita.'); return; }
        set('avoided_categories', evitadas.includes(id) ? evitadas.filter(x => x !== id) : [...evitadas, id]);
    };
    const alternarAlimento = (nombre) => {
        const kw = aPalabra(nombre);
        if (!kw) return;
        set('avoided_keywords', palabras.includes(kw) ? palabras.filter(k => k !== kw) : [...palabras, kw]);
    };
    const anadirPalabra = async () => {
        const kw = aPalabra(buscando);
        if (!kw) return;
        if (palabras.includes(kw)) { toast.error(`«${kw}» ya está en la lista`); return; }
        set('avoided_keywords', [...palabras, kw]);
        setBuscando('');
        try {
            const res = await api.get(`/calculator/search?q=${encodeURIComponent(kw)}&limit=1`);
            if (!(res.data?.alimentos || []).length) {
                toast.warning(`«${kw}» no coincide con ningún alimento del catálogo: guardada, pero hoy no bloquea nada.`);
            }
        } catch (e) { /* la palabra ya está guardada; un error de red no aporta nada aquí */ }
    };
    // LAS SUGERENCIAS MIENTRAS ESCRIBE (04-11): el mismo comportamiento que el buscador de
    // «+ Apuntar un extra» -- desde 2 letras, con su respiro de 300 ms, contra el catálogo --.
    // Elegir una sugerencia la quita igual que el botón de la lista de su categoría; lo
    // escrito a mano con «Añadir» sigue valiendo para lo que no esté en el catálogo.
    const [sugerencias, setSugerencias] = useState([]);
    const [sugiriendo, setSugiriendo] = useState(false);
    const [verSugerencias, setVerSugerencias] = useState(false);
    const cajaBuscador = useRef(null);
    useEffect(() => {
        const q = buscando.trim();
        if (q.length < 2) { setSugerencias([]); setSugiriendo(false); return undefined; }
        let vivo = true;
        setSugiriendo(true);
        const t = setTimeout(() => {
            api.get('/calculator/search', { params: { q, limit: 30 } })
                .then(r => { if (vivo) { setSugerencias(r.data?.alimentos || []); setSugiriendo(false); } })
                .catch(() => { if (vivo) { setSugerencias([]); setSugiriendo(false); } });
        }, 300);
        return () => { vivo = false; clearTimeout(t); };
    }, [buscando, api]);
    useEffect(() => {
        if (!verSugerencias) return undefined;
        const fuera = (e) => {
            if (cajaBuscador.current && !cajaBuscador.current.contains(e.target)) setVerSugerencias(false);
        };
        document.addEventListener('pointerdown', fuera);
        return () => document.removeEventListener('pointerdown', fuera);
    }, [verSugerencias]);
    const elegirSugerencia = (a) => {
        const kw = aPalabra(a.nombre);
        if (!kw) return;
        if (!palabras.includes(kw)) set('avoided_keywords', [...palabras, kw]);
        setBuscando('');
        setVerSugerencias(false);
    };
    const abrirSugerencias = verSugerencias && buscando.trim().length >= 2;

    const preparaciones = PREPARATIONS.filter(pr => lista.preps.includes(pr.value));
    const entera = cat && evitadas.includes(cat.id);
    return (
        <div className="space-y-5">
            <SelectorDeCategoria categorias={categorias} elegida={abierta} onElegir={elegir}
                estado={(id) => (evitadas.includes(id) ? { texto: 'Fuera', tono: 'rojo' } : null)} />

            {cat && (
                <div className="rounded-2xl border border-[#262626] bg-[#161616] p-4 space-y-4" data-testid="exclusiones-categoria">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <p className="font-bold text-[#f2f0ed]">{cat.label}</p>
                        {vetable(cat.id) ? (
                            <button type="button" onClick={() => alternarCategoria(cat.id)} data-testid="exclusiones-categoria-entera"
                                className={`rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                                    entera ? 'bg-red-500/15 text-red-400 border border-red-500/60'
                                        : 'bg-[#222] text-[#d9d5d0] hover:text-white border border-transparent'}`}>
                                {entera ? 'Toda la categoría fuera · deshacer' : 'No quiero nada de esta categoría'}
                            </button>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs text-[#8a8580]">
                                <Lock className="w-3.5 h-3.5" /> Se ofrecen siempre
                            </span>
                        )}
                    </div>
                    {entera ? (
                        <p className="text-sm text-[#a8a29c]">No te pondremos nada de esta categoría.</p>
                    ) : (
                        <>
                            <FiltrosDePreparacion disponibles={preparaciones} marcados={preps} onCambiar={setPreps} />
                            <ListaDeAlimentos lista={lista} fila={(a) => {
                                const fuera = palabras.includes(aPalabra(a.nombre));
                                return (
                                    <div className="flex items-center justify-between gap-3 py-2.5">
                                        <span className={`text-sm ${fuera ? 'line-through text-red-400' : 'text-[#f2f0ed]'}`}>{a.nombre}</span>
                                        <button type="button" onClick={() => alternarAlimento(a.nombre)}
                                            className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                                fuera ? 'bg-red-500/15 text-red-400 border border-red-500/60'
                                                    : 'bg-[#222] text-[#d9d5d0] hover:text-white border border-transparent'}`}>
                                            {fuera ? 'Deshacer' : 'No lo quiero'}
                                        </button>
                                    </div>
                                );
                            }} />
                        </>
                    )}
                </div>
            )}

            <div>
                <p className="text-sm text-foreground/60 mb-2">O un alimento concreto, por su nombre:</p>
                <div className="flex gap-2">
                    <div className="relative flex-1 min-w-0" ref={cajaBuscador}>
                        <Search className="w-4 h-4 text-[#5f5a55] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <Input value={buscando}
                            onChange={e => { setBuscando(e.target.value); setVerSugerencias(true); }}
                            onFocus={() => setVerSugerencias(true)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); setVerSugerencias(false); anadirPalabra(); }
                                if (e.key === 'Escape') setVerSugerencias(false);
                            }}
                            placeholder="Por ejemplo: atún" data-testid="exclusiones-buscador"
                            autoComplete="off" role="combobox" aria-expanded={abrirSugerencias}
                            aria-controls="exclusiones-sugerencias"
                            className={`${CAMPO} pl-10`} />
                        {abrirSugerencias && (
                            <div id="exclusiones-sugerencias" role="listbox" data-testid="exclusiones-sugerencias"
                                className="absolute z-40 mt-2 w-full max-h-[280px] overflow-y-auto rounded-[14px] border border-[#2a2a2a] bg-[#111] shadow-2xl py-1">
                                {sugiriendo && <p className="px-3 py-2.5 text-sm text-[#8a8580]">Buscando…</p>}
                                {!sugiriendo && sugerencias.length === 0 && (
                                    <p className="px-3 py-2.5 text-sm text-[#8a8580]">
                                        Eso no está en el catálogo. Pulsa «Añadir» para apuntarlo igual.
                                    </p>
                                )}
                                {!sugiriendo && sugerencias.map((a, n) => {
                                    const ya = palabras.includes(aPalabra(a.nombre));
                                    return (
                                        <button key={a.id ?? n} type="button" role="option" aria-selected={ya}
                                            onClick={() => elegirSugerencia(a)}
                                            data-testid={`exclusiones-sugerencia-${a.id ?? n}`}
                                            className="w-full px-3 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-[#1c1c1c]">
                                            <span className={`text-sm truncate ${ya ? 'line-through text-red-400' : 'text-[#f2f0ed]'}`}>{a.nombre}</span>
                                            {ya && <span className="flex-none text-[11px] font-bold uppercase tracking-wide text-red-400">Fuera</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <Button type="button" variant="outline" onClick={() => { setVerSugerencias(false); anadirPalabra(); }}
                        className="h-[52px] rounded-[12px] border-[#333] text-foreground flex-shrink-0">Añadir</Button>
                </div>
                {(palabras.length > 0 || evitadas.length > 0) && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid="exclusiones-elegidas">
                        {evitadas.map(id => {
                            const c = categorias.find(x => x.id === id);
                            return (
                                <Pastilla key={`c-${id}`} tono="rojo" onQuitar={() => alternarCategoria(id)}
                                    etiquetaQuitar={`Quitar ${c ? c.label : id}`}>
                                    {c ? c.short || c.label : id}
                                </Pastilla>
                            );
                        })}
                        {palabras.map(kw => (
                            <Pastilla key={kw} tono="rojo" etiquetaQuitar={`Quitar ${kw}`}
                                onQuitar={() => set('avoided_keywords', palabras.filter(k => k !== kw))}>
                                {kw}
                            </Pastilla>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PANTALLA 2 · LO QUE MÁS LE GUSTA
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * `onCuadra(true | false | null)`: si con lo marcado se llega a 20 g de proteína y 20 g de
 * hidratos. `null` mientras se pregunta o si la llamada falla: un fallo de red no puede
 * dejar a nadie sin continuar.
 */
export const PreferidosDelAlta = ({ answers, set, api, onCuadra }) => {
    const [cuadra, setCuadra] = useState(null);
    const evitadas = answers.avoided_categories || [];
    const palabrasFuera = answers.avoided_keywords || [];
    const preferidas = preferidasDePartida(answers);
    // Lo que quitó en la pantalla anterior no se ofrece aquí: no puede gustarle y evitarse.
    const categorias = useMemo(() => conNombreCorto(PREFERENCE_CATEGORIES).filter(c => !evitadas.includes(c.id)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [evitadas.join(',')]);

    // SOLO CATEGORÍAS (04-11): en esta pantalla ya no se eligen alimentos sueltos ni se
    // enseñan en ningún sitio. Los que trajera un borrador de antes se vacían al llegar:
    // no se ven, así que tampoco pueden contar sin que él lo sepa.
    // Y las de partida, escritas en las respuestas en cuanto se llega: así viajan aunque no
    // toque nada, y el borrador las recuerda.
    useEffect(() => {
        const ahora = Array.isArray(answers.food_preferences) ? answers.food_preferences : null;
        if (!ahora || ahora.join(',') !== preferidas.join(',')) set('food_preferences', preferidas);
        if (Array.isArray(answers.preferred_foods) && answers.preferred_foods.length) set('preferred_foods', []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // LOS 20 g, EN VIVO (la misma comprobación que Preferencias, doc 23-08, P20), con lo que
    // ha quitado en la pantalla anterior.
    useEffect(() => {
        let vivo = true;
        setCuadra(null);
        onCuadra?.(null);
        const t = setTimeout(() => {
            api.post('/calculator/preferencias/cuadra', preguntaDeLos20g({
                marcadas: preferidas,
                evitarCategorias: evitadas,
                evitarPalabras: palabrasFuera,
                preferidos: [],
            })).then(r => {
                if (!vivo) return;
                setCuadra(r.data || null);
                onCuadra?.(r.data ? !noLlegaALos20g(r.data) : null);
            }).catch(() => { if (vivo) { setCuadra(null); onCuadra?.(null); } });
        }, 500);
        return () => { vivo = false; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferidas.join(','), evitadas.join(','), palabrasFuera.join(',')]);

    const fija = (id) => OBLIGATORY_PREFERENCES.includes(id);
    const alternarCategoria = (id) => {
        if (fija(id)) { toast.info('Las grasas de buena calidad van siempre: el método las necesita en todas las comidas.'); return; }
        set('food_preferences', preferidas.includes(id) ? preferidas.filter(x => x !== id) : [...preferidas, id]);
    };
    const noCuadra = noLlegaALos20g(cuadra);

    return (
        <div className="space-y-5">
            {/* El selector marca y desmarca directamente, varias seguidas, sin abrir la
                categoría ni enseñar sus alimentos. */}
            <SelectorDeCategoria categorias={categorias} onElegir={alternarCategoria} varias sinEjemplos
                rotulo="Elige tus categorías preferidas"
                estado={(id) => (preferidas.includes(id) && !fija(id)
                    ? { texto: 'Preferida', tono: 'naranja' } : null)} />

            <div className="flex flex-wrap gap-2" data-testid="preferidos-elegidos">
                {preferidas.map(id => {
                    const c = PREFERENCE_CATEGORIES.find(x => x.id === id);
                    return (
                        <Pastilla key={`c-${id}`} etiquetaQuitar={`Quitar ${c ? c.label : id}`}
                            onQuitar={fija(id) ? null : () => alternarCategoria(id)}>
                            {c ? conNombreCorto([c])[0].short : id}
                        </Pastilla>
                    );
                })}
            </div>

            {cuadra && (
                <div data-testid="preferidos-cuadra">
                    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#8a8580] mb-1">
                        {/* El título dice lo que pasa (test 4.8 de Jesús): con algo en rojo
                            debajo no puede decir «se puede crear». */}
                        {noCuadra
                            ? 'Con lo que has marcado no se puede crear, como mínimo'
                            : 'Con lo que has marcado se puede crear, como mínimo'}
                    </p>
                    <div className="flex gap-4 text-sm font-semibold">
                        <span className={cuadra.proteina ? 'text-green-400' : 'text-red-400'}>
                            {cuadra.proteina ? '✓' : '✗'} 20 g de proteína
                        </span>
                        <span className={cuadra.hidratos ? 'text-green-400' : 'text-red-400'}>
                            {cuadra.hidratos ? '✓' : '✗'} 20 g de hidratos
                        </span>
                    </div>
                    {noCuadra && (
                        <p className="text-sm text-red-400 mt-1.5">
                            Marca alguna categoría más para llegar a los 20 g de
                            {[!cuadra.proteina && ' proteína', !cuadra.hidratos && ' hidratos'].filter(Boolean).join(' y')}.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};
